import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { Notification } from "@/types";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  addNotification: (notification: Omit<Notification, "id" | "createdAt" | "read">) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error("useNotifications must be used within a NotificationProvider");
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { toast } = useToast();
  const { t } = useTranslation();

  const unreadCount = notifications.filter((n) => !n.read).length;

  const addNotification = useCallback((newNotification: Omit<Notification, "id" | "createdAt" | "read">) => {
    const notification: Notification = {
      ...newNotification,
      id: Math.random().toString(36).substring(2, 9),
      createdAt: new Date().toISOString(),
      read: false,
    };

    setNotifications((prevNotifications) => [notification, ...prevNotifications]);

    // Показываем как toast
    toast({
      title: notification.type === "error" ? t('common.error') : t('common.notification'),
      description: notification.message,
      variant: notification.type === "error" ? "destructive" : "default",
    });

    console.log("NotificationContext: добавлено уведомление", notification.message);
  }, [toast, t]);

  const markAsRead = useCallback((id: string) => {
    setNotifications((prevNotifications) =>
      prevNotifications.map((notification) => (notification.id === id ? { ...notification, read: true } : notification))
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prevNotifications) => prevNotifications.map((notification) => ({ ...notification, read: true })));
  }, []);

  // Load notifications from local storage on initial render
  useEffect(() => {
    const storedNotifications = localStorage.getItem("notifications");
    if (storedNotifications) {
      try {
        setNotifications(JSON.parse(storedNotifications));
      } catch (e) {
        console.error("Failed to load notifications from localStorage", e);
      }
    }
  }, []);

  // Save notifications to local storage when they change
  useEffect(() => {
    localStorage.setItem("notifications", JSON.stringify(notifications));
  }, [notifications]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      markAsRead,
      markAllAsRead,
      addNotification,
    }),
    [notifications, unreadCount, markAsRead, markAllAsRead, addNotification]
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};
